import get from 'lodash/get';
import omit from 'lodash/omit';
import {useEffect,useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate,useSearchParams} from 'react-router-dom';

import LoginForm from '../../components/auth/LoginForm';

import config from '../../config/config';
import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import useUser from '../../hooks/useUser';
import useMFANeeded from '../../hooks/useMFANeeded';
import {
  useCreateMFACodeMutation,
  useLoginMutation
} from '../../redux/services/authApi';
import {useLazyGetCurrentUserQuery} from '../../redux/services/userApi';
import {setCredentials, clearDeviceToken} from '../../redux/reducers/auth';
import AuthLayout from "../../components/auth/AuthLayout";
import {shuffle} from "../../helpers/signupRandomBackground";

const parseMFARoute = ({redirectPath = null} = {})  => {
  if (!redirectPath) return '/mfa';
  return `/mfa?redirectPath=${encodeURIComponent(redirectPath)}`;
};

const handleSubmit = ({
  props,
  login,
  setError,
  fetchUser,
  onSuccess,
}) => async formData => {
  const res = await login(formData);
  const {data} = res;
  if (res.error && res.error.status) {
    setError({ message: get(res, 'error.data.detail', 'Unknown error') });
  } else {
    setError(null);
    props.setCredentials({
      email: formData.email,
      user: omit({...data}, 'refresh', 'access'),
      refreshToken: get(data, 'refresh'),
      accessToken: get(data, 'access'),
    });
    onSuccess({...formData});
  }
};

const handleSuccess = ({
  props, 
  mfaNeeded, 
  createMFACode,
  navigate,
  redirectPath = null,
}) => async (formData) => {
  const isDifferentUser = props.priorUserEmail !== formData.email;
  if (isDifferentUser) props.clearDeviceToken();
  if (mfaNeeded || isDifferentUser) {
    const codeReq = await createMFACode({email: formData.email});
    navigate(parseMFARoute({redirectPath}));
    return;
  }
  navigate(redirectPath || '/');
};

const LoginPage = props => {
  // const {auth} = props;
  const [isDifferentUser, setIsDifferentUser] = useState(false);
  const [error, setError] = useState(null);
  const [login] = useLoginMutation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirectPath');
  const isLoggedIn = useIsLoggedIn();
  const hasUser = !!useUser();
  const mfaNeeded = useMFANeeded();
  const [createMFACode, req] = useCreateMFACodeMutation();

  // redirect if the user is already logged in
  useEffect(() => {
    const shouldNavigateHome = isLoggedIn && hasUser && !mfaNeeded;
    const shouldNavigateToMFA = isLoggedIn && hasUser && mfaNeeded;
    if (shouldNavigateHome) navigate(redirectPath || '/');
    if (shouldNavigateToMFA) navigate(parseMFARoute({redirectPath}));
  }, [isLoggedIn, hasUser, mfaNeeded, navigate]);

  useEffect(() => {
    shuffle()
  }, []);

  const childProps = {
    ...props,
    error,
    onSubmit: handleSubmit({
      props,
      setError,
      login,
      onSuccess: handleSuccess({
        props,
        mfaNeeded, 
        navigate, 
        createMFACode,
        redirectPath,
      }),
    }),
    title: 'Login',
    defaultEmail: props.priorUserEmail,
    showStepHeader: false,
    showLoginBtn: false,
  };

  return <AuthLayout {...childProps}>
    <LoginForm {...childProps} />
  </AuthLayout>;
};


const mapState = state => ({
  // auth: selectCurrentUser()(state),
  priorUserEmail: get(state, 'auth.deviceEmail') || null,
});

const mapDispatch = dispatch => ({
  setCredentials: params => dispatch(setCredentials(params)),
  clearDeviceToken: params => dispatch(clearDeviceToken(params)),
})

export default connect(mapState, mapDispatch)(LoginPage);
